"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import BrainMobileAbout from "./mobile/BrainMobileAbout";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./my-stream/MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./my-stream/MyStreamWaveOutcome";
import { WaveWinners } from "../waves/winners/WaveWinners";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import MyStreamWaveMyVotes from "./my-stream/votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./my-stream/MyStreamWaveFAQ";
import MyStreamWaveSales from "./my-stream/MyStreamWaveSales";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import BrainMobileWaves from "./mobile/BrainMobileWaves";
import BrainMobileMessages from "./mobile/BrainMobileMessages";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import BrainNotifications from "./notifications/NotificationsContainer";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
export enum BrainView {
  DEFAULT = "DEFAULT",
  ABOUT = "ABOUT",
  LEADERBOARD = "LEADERBOARD",
  SALES = "SALES",
  WINNERS = "WINNERS",
  OUTCOME = "OUTCOME",
  MY_VOTES = "MY_VOTES",
  FAQ = "FAQ",
  WAVES = "WAVES",
  MESSAGES = "MESSAGES",
  NOTIFICATIONS = "NOTIFICATIONS",
}
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(132);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const [hydrated, setHydrated] = useState(false);
  const myStream = useMyStreamOptional();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHydrated(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [activeView, setActiveView] = useState(BrainView.DEFAULT);
  let t3;
  if ($[2] !== searchParams) {
    t3 = searchParams.get("drop") ?? undefined;
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dropId = t3;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t4;
  let t5;
  if ($[4] !== effectiveDropId) {
    t4 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t5 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[4] = effectiveDropId;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  const t6 = !!effectiveDropId;
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      placeholderData: keepPreviousData,
      enabled: t6
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data: drop
  } = useQuery(t7);
  let t8;
  if ($[11] !== myStream?.activeWave.id || $[12] !== pathname || $[13] !== searchParams) {
    t8 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[11] = myStream?.activeWave.id;
    $[12] = pathname;
    $[13] = searchParams;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const waveId = t8;
  let t9;
  if ($[15] !== isApp || $[16] !== pathname || $[17] !== router || $[18] !== searchParams) {
    t9 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[15] = isApp;
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== waveId) {
    t10 = {
      waveId,
      onWaveNotFound: t9
    };
    $[20] = t9;
    $[21] = waveId;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const {
    data: wave
  } = useWaveData(t10);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave
  } = useWave(wave);
  const {
    voting: t11,
    decisions: t12
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t11;
  const {
    firstDecisionDone
  } = t12;
  let t13;
  if ($[23] !== pathname || $[24] !== router || $[25] !== searchParams) {
    t13 = drop_0 => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop_0.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[23] = pathname;
    $[24] = router;
    $[25] = searchParams;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const onDropClick = t13;
  let t14;
  if ($[27] !== beginClosingDrop || $[28] !== dropId || $[29] !== pathname || $[30] !== router || $[31] !== searchParams) {
    t14 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[27] = beginClosingDrop;
    $[28] = dropId;
    $[29] = pathname;
    $[30] = router;
    $[31] = searchParams;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const onDropClose = t14;
  let t15;
  if ($[33] !== drop || $[34] !== effectiveDropId) {
    t15 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[33] = drop;
    $[34] = effectiveDropId;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const isDropOpen = t15;
  const hasWave = Boolean(waveId);
  let t16;
  let t17;
  if ($[36] !== isApp || $[37] !== pathname || $[38] !== searchParams || $[39] !== waveId) {
    t16 = () => {
      const viewParam = searchParams.get("view");
      const createParam = searchParams.get("create");
      if (createParam && isApp) {
        setActiveView(BrainView.DEFAULT);
        return;
      }
      if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
        setActiveView(BrainView.NOTIFICATIONS);
        return;
      }
      if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
        setActiveView(BrainView.MESSAGES);
        return;
      }
      if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
        setActiveView(BrainView.WAVES);
        return;
      }
      if (pathname === "/" && !waveId && !viewParam) {
        setActiveView(BrainView.DEFAULT);
      }
    };
    t17 = [pathname, searchParams, waveId, isApp];
    $[36] = isApp;
    $[37] = pathname;
    $[38] = searchParams;
    $[39] = waveId;
    $[40] = t16;
    $[41] = t17;
  } else {
    t16 = $[40];
    t17 = $[41];
  }
  useEffect(t16, t17);
  let t18;
  if ($[42] !== activeView || $[43] !== firstDecisionDone || $[44] !== hasWave || $[45] !== isCompleted || $[46] !== isCurationWave || $[47] !== isMemesWave || $[48] !== pathname || $[49] !== wave || $[50] !== waveId) {
    t18 = () => {
      const globalViews = new Set([BrainView.DEFAULT, BrainView.WAVES, BrainView.MESSAGES, BrainView.NOTIFICATIONS]);
      const routeToView = {
        "/waves": BrainView.WAVES,
        "/messages": BrainView.MESSAGES,
        "/notifications": BrainView.NOTIFICATIONS
      };
      if (!hasWave) {
        const isWaveSpecificView = !globalViews.has(activeView);
        if (isWaveSpecificView) {
          setActiveView(routeToView[pathname ?? ""] ?? BrainView.DEFAULT);
        }
        return;
      }
      if (!wave) {
        return;
      }
      const shouldResetToDefault = activeView === BrainView.LEADERBOARD && isCompleted || activeView === BrainView.SALES && !isCurationWave || activeView === BrainView.WINNERS && !firstDecisionDone || activeView === BrainView.MY_VOTES && !isMemesWave && !isCurationWave || activeView === BrainView.FAQ && !isMemesWave;
      if (shouldResetToDefault) {
        setActiveView(BrainView.DEFAULT);
        return;
      }
      const nonWaveViews = new Set([BrainView.NOTIFICATIONS, BrainView.MESSAGES, BrainView.WAVES]);
      if (waveId && nonWaveViews.has(activeView)) {
        setActiveView(BrainView.DEFAULT);
      }
    };
    $[42] = activeView;
    $[43] = firstDecisionDone;
    $[44] = hasWave;
    $[45] = isCompleted;
    $[46] = isCurationWave;
    $[47] = isMemesWave;
    $[48] = pathname;
    $[49] = wave;
    $[50] = waveId;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== activeView || $[53] !== firstDecisionDone || $[54] !== hasWave || $[55] !== isCompleted || $[56] !== isCurationWave || $[57] !== isMemesWave || $[58] !== isRankWave || $[59] !== pathname || $[60] !== wave || $[61] !== waveId) {
    t19 = [hasWave, wave, isCompleted, firstDecisionDone, activeView, isMemesWave, isCurationWave, isRankWave, waveId, pathname];
    $[52] = activeView;
    $[53] = firstDecisionDone;
    $[54] = hasWave;
    $[55] = isCompleted;
    $[56] = isCurationWave;
    $[57] = isMemesWave;
    $[58] = isRankWave;
    $[59] = pathname;
    $[60] = wave;
    $[61] = waveId;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  useEffect(t18, t19);
  let t20;
  if ($[63] !== pathname || $[64] !== router || $[65] !== searchParams) {
    t20 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname ?? "/";
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[63] = pathname;
    $[64] = router;
    $[65] = searchParams;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const closeCreateOverlay = t20;
  let t21;
  bb0: {
    if (!isApp) {
      t21 = null;
      break bb0;
    }
    const createParam_0 = searchParams.get("create");
    if (!createParam_0) {
      t21 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t21 = null;
      break bb0;
    }
    if (createParam_0 === "dm") {
      let t22;
      if ($[67] !== closeCreateOverlay || $[68] !== connectedProfile) {
        t22 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[67] = closeCreateOverlay;
        $[68] = connectedProfile;
        $[69] = t22;
      } else {
        t22 = $[69];
      }
      t21 = t22;
      break bb0;
    }
    if (createParam_0 === "wave") {
      let t22;
      if ($[70] !== closeCreateOverlay || $[71] !== connectedProfile) {
        t22 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[70] = closeCreateOverlay;
        $[71] = connectedProfile;
        $[72] = t22;
      } else {
        t22 = $[72];
      }
      t21 = t22;
      break bb0;
    }
    t21 = null;
  }
  const createOverlay = t21;
  let t22;
  if ($[73] !== waveId) {
    t22 = <BrainMobileAbout activeWaveId={waveId} />;
    $[73] = waveId;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== isRankWave || $[76] !== onDropClick || $[77] !== wave) {
    t23 = isRankWave && !!wave ? <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} /> : null;
    $[75] = isRankWave;
    $[76] = onDropClick;
    $[77] = wave;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== isCurationWave || $[80] !== wave) {
    t24 = isCurationWave && !!wave ? <MyStreamWaveSales waveId={wave.id} /> : null;
    $[79] = isCurationWave;
    $[80] = wave;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== isRankWave || $[83] !== onDropClick || $[84] !== wave) {
    t25 = isRankWave && !!wave ? <div className="tw-px-2 sm:tw-px-4"><WaveWinners wave={wave} onDropClick={onDropClick} /></div> : null;
    $[82] = isRankWave;
    $[83] = onDropClick;
    $[84] = wave;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  let t26;
  if ($[86] !== isRankWave || $[87] !== wave) {
    t26 = isRankWave && !!wave ? <MyStreamWaveOutcome wave={wave} /> : null;
    $[86] = isRankWave;
    $[87] = wave;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  let t27;
  if ($[89] !== isRankWave || $[90] !== onDropClick || $[91] !== wave) {
    t27 = isRankWave && !!wave ? <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} /> : null;
    $[89] = isRankWave;
    $[90] = onDropClick;
    $[91] = wave;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let t28;
  if ($[93] !== isMemesWave || $[94] !== isRankWave || $[95] !== wave) {
    t28 = isRankWave && isMemesWave && !!wave ? <MyStreamWaveFAQ wave={wave} /> : null;
    $[93] = isMemesWave;
    $[94] = isRankWave;
    $[95] = wave;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <BrainMobileWaves />;
    $[97] = t29;
  } else {
    t29 = $[97];
  }
  let t30;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <BrainMobileMessages />;
    $[98] = t30;
  } else {
    t30 = $[98];
  }
  let t31;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <BrainNotifications />;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  let t32;
  if ($[100] !== children || $[101] !== t22 || $[102] !== t23 || $[103] !== t24 || $[104] !== t25 || $[105] !== t26 || $[106] !== t27 || $[107] !== t28) {
    t32 = {
      [BrainView.ABOUT]: t22,
      [BrainView.DEFAULT]: children,
      [BrainView.LEADERBOARD]: t23,
      [BrainView.SALES]: t24,
      [BrainView.WINNERS]: t25,
      [BrainView.OUTCOME]: t26,
      [BrainView.MY_VOTES]: t27,
      [BrainView.FAQ]: t28,
      [BrainView.WAVES]: t29,
      [BrainView.MESSAGES]: t30,
      [BrainView.NOTIFICATIONS]: t31
    };
    $[100] = children;
    $[101] = t22;
    $[102] = t23;
    $[103] = t24;
    $[104] = t25;
    $[105] = t26;
    $[106] = t27;
    $[107] = t28;
    $[108] = t32;
  } else {
    t32 = $[108];
  }
  const viewComponents = t32;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t33;
  if ($[109] !== drop || $[110] !== dropOverlayClass || $[111] !== isDropOpen || $[112] !== onDropClose) {
    t33 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[109] = drop;
    $[110] = dropOverlayClass;
    $[111] = isDropOpen;
    $[112] = onDropClose;
    $[113] = t33;
  } else {
    t33 = $[113];
  }
  let t34;
  if ($[114] !== activeView || $[115] !== hasWave || $[116] !== hydrated || $[117] !== isApp || $[118] !== wave) {
    t34 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={setActiveView} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[114] = activeView;
    $[115] = hasWave;
    $[116] = hydrated;
    $[117] = isApp;
    $[118] = wave;
    $[119] = t34;
  } else {
    t34 = $[119];
  }
  let t35;
  let t36;
  let t37;
  let t38;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      opacity: 0,
      x: 20
    };
    t36 = {
      opacity: 1,
      x: 0
    };
    t37 = {
      opacity: 0,
      x: -20
    };
    t38 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[120] = t35;
    $[121] = t36;
    $[122] = t37;
    $[123] = t38;
  } else {
    t35 = $[120];
    t36 = $[121];
    t37 = $[122];
    t38 = $[123];
  }
  const t39 = viewComponents[activeView];
  let t40;
  if ($[124] !== activeView || $[125] !== t39) {
    t40 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t35} animate={t36} exit={t37} transition={t38} className="tw-min-w-0 tw-flex-1">{t39}</motion.div></AnimatePresence>;
    $[124] = activeView;
    $[125] = t39;
    $[126] = t40;
  } else {
    t40 = $[126];
  }
  let t41;
  if ($[127] !== createOverlay || $[128] !== t33 || $[129] !== t34 || $[130] !== t40) {
    t41 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t33}{t34}{t40}</div>;
    $[127] = createOverlay;
    $[128] = t33;
    $[129] = t34;
    $[130] = t40;
    $[131] = t41;
  } else {
    t41 = $[131];
  }
  return t41;
};
export default BrainMobile;